import { PartnershipEligibilityDisclaimer } from '@/components/PartnershipEligibilityDisclaimer';
import { PartnershipEligibleBadge } from '@/components/PartnershipEligibleBadge';
import { ProductDetails } from '@/components/ProductDetails';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { AM_BEST_CARRIER_RATINGS, AM_BEST_RATING_INFO } from '@/util/amBestRatingData';
import { getCarrierLogoSrc } from '@/util/carrierLogo';
import { usePolicyLoadingState } from '@/features/shop-insurance/hooks/use-policy-loading-state';
import { useQuoteCustomization } from '@/features/shop-insurance/hooks/use-quote-customization';
import type { TraditionalLTCQuoteWithROI } from '@/features/shop-insurance/types';
import { getPolicyTypeFromTraditionalLTCQuote } from '@shared/getPolicyTypeFromTraditionalLTCQuote';
import { cn } from '@client/lib';
import type { Quote } from '@client/model/quotesModel';
import { selectClient, selectClientCalculationSettings } from '@client/model/selectors';
import { isLifeInsuranceQuote, isTraditionalLTCQuote } from '@client/util/quoteTypeGuards';
import { formatCurrency, getQuotePaymentOptionDisplayLabel, zipCodeToState } from '@shared';
import { LtcCostDisplay } from '@client/components/LtcCostDisplay';
import { Check, ChevronDown, ChevronRight, Loader2, Settings } from 'lucide-react';
import { useSelector } from 'react-redux';
import { CustomizationPanel } from './CustomizationPanel';
import { TraditionalLTCProductDetails } from './TraditionalLTCProductDetails';

interface QuoteWithROI extends Quote {
  roi?: number;
  formattedROI?: string;
  projectedCostCoverage?: number;
}

type PolicyQuote = QuoteWithROI | TraditionalLTCQuoteWithROI;

interface PolicyQuoteCardProps {
  quote: PolicyQuote;
  allCarrierQuotes?: QuoteWithROI[] | TraditionalLTCQuoteWithROI[];
  isExpanded: boolean;
  isSelected: boolean;
  onToggleExpand: () => void;
  onAddToPlan: (quote: PolicyQuote) => void;
  onCompare: () => void;
}

function getCarrierLogo(carrierName: string): string {
  return getCarrierLogoSrc(carrierName);
}

function formatMaritalClass(maritalClass: string): string | null {
  if (maritalClass === 'Married 1 Buy') {
    return 'Individual Coverage';
  }
  if (maritalClass === 'Married 2 Buy') {
    return 'Joint Coverage';
  }
  if (maritalClass === 'Single') {
    return 'Single';
  }
  return null;
}

/**
 * Get coverage type label for a quote
 */
function getCoverageTypeLabel(quote: PolicyQuote): string | null {
  if ('maritalClass' in quote && quote.maritalClass) {
    return formatMaritalClass(quote.maritalClass);
  }
  if ('policyType' in quote && quote.policyType) {
    if (quote.policyType === 'Joint Life') return 'Joint Coverage';
    if (quote.policyType === 'Single Life') return 'Individual Coverage';
    if (quote.policyType === 'Couples Discount') return 'Couples Discount';
  }
  return null;
}

/**
 * Get the cost label based on payment type
 */
function getCostLabel(quote: PolicyQuote): string {
  // Check if it's a traditional LTC quote
  if ('premiumFrequency' in quote) {
    const freq = quote.premiumFrequency?.toLowerCase();
    if (freq === 'monthly') return 'Monthly Cost';
    if (freq === 'annual' || freq === 'annually') return 'Annual Cost';
    return 'Cost';
  }

  // Life insurance quote
  const paymentOption = getQuotePaymentOptionDisplayLabel(quote);
  if (paymentOption === 'Single Pay' || paymentOption?.endsWith('-Pay')) return paymentOption;
  if (paymentOption === 'Monthly') return 'Monthly Cost';
  return 'Annual Cost';
}

function getCostValue(quote: PolicyQuote): number {
  if (isTraditionalLTCQuote(quote)) {
    return quote.primaryModalPremium;
  }
  return quote.premiumAmount || 0;
}

function getProjectedCoverage(quote: PolicyQuote): number {
  if ('projectedCostCoverage' in quote) {
    return quote.projectedCostCoverage || 0;
  }
  return 0;
}

export function PolicyQuoteCard({
  quote,
  allCarrierQuotes,
  isExpanded,
  isSelected,
  onToggleExpand,
  onAddToPlan,
  onCompare,
}: PolicyQuoteCardProps) {
  const client = useSelector(selectClient);
  const clientCalculationSettings = useSelector(selectClientCalculationSettings);
  const returnOnInvestmentCalculationType =
    clientCalculationSettings?.returnOnInvestmentCalculationType || 'total';

  const isLifeInsurance = isLifeInsuranceQuote(quote);
  const hasCustomization = isLifeInsurance && allCarrierQuotes && allCarrierQuotes.length > 0;

  // Use custom hook for loading state tracking
  const { isLoading } = usePolicyLoadingState({ quote, client });

  const customization = useQuoteCustomization({
    allCarrierQuotes: (isLifeInsurance && allCarrierQuotes
      ? allCarrierQuotes
      : [quote]) as QuoteWithROI[],
    topQuote: quote as QuoteWithROI,
    client,
    returnOnInvestmentCalculationType,
  });

  const displayQuote = hasCustomization ? customization.displayQuote : quote;

  const carrierName =
    ('carrierName' in displayQuote ? displayQuote.carrierName : displayQuote.carrier) || '';
  const productName = displayQuote.productName || '';
  const rating = AM_BEST_CARRIER_RATINGS[carrierName];
  const carrierLogo = getCarrierLogo(carrierName);
  const costLabel = getCostLabel(displayQuote);
  const costValue = getCostValue(displayQuote);
  const projectedCoverage = getProjectedCoverage(displayQuote);
  const displayROI = displayQuote.formattedROI;
  const coverageTypeLabel = getCoverageTypeLabel(displayQuote);

  const handleTogglePolicy = (event: React.MouseEvent) => {
    event.stopPropagation();
    onAddToPlan(displayQuote);
  };

  return (
    <Card
      className={cn(
        'group relative w-full overflow-hidden rounded-xl transition-all duration-200',
        isSelected
          ? 'border-primary shadow-primary/20 ring-primary/20 border-2 shadow-lg ring-2'
          : 'border-primary/20 hover:border-primary/40 border hover:shadow-xl hover:shadow-black/5',
      )}
    >
      {/* Selected Badge */}
      {isSelected && (
        <div className="bg-primary absolute right-0 top-0 flex items-center gap-1 rounded-bl-lg px-3 py-1 text-xs font-medium text-white">
          <Check className="h-3 w-3" />
          In Your Plan
        </div>
      )}

      <div className="flex flex-col">
        {/* Header Row: Product Info + Metrics */}
        <div className={cn('flex items-start justify-between gap-8 p-6', isSelected && 'pt-8')}>
          {/* Product Info Section */}
          <div className="flex items-start gap-3">
            <div className="size-16 shrink-0 select-none rounded-lg border border-gray-200 bg-white p-2 shadow-sm">
              <img
                src={carrierLogo}
                alt={carrierName}
                className="size-full object-contain"
              />
            </div>
            <div className="flex flex-col gap-2">
              {rating && (
                <TooltipProvider delayDuration={0}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="text-primary text-xs font-medium">Credit Rating {rating}</div>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-sm rounded-lg border-0 bg-black/90 px-4 py-3.5 shadow-none">
                      <div className="flex flex-col gap-1">
                        <p className="text-sm font-bold text-white">
                          {AM_BEST_RATING_INFO[rating]?.title}
                        </p>
                        <p className="text-sm text-white/80">
                          {AM_BEST_RATING_INFO[rating]?.description}
                        </p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              )}
              <h2 className="text-lg font-semibold text-gray-900">{productName}</h2>
              <div className="flex items-center gap-2">
                <div className="text-xs text-gray-700">{carrierName}</div>
                {coverageTypeLabel && (
                  <Badge className="rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700 hover:bg-blue-100">
                    {coverageTypeLabel}
                  </Badge>
                )}
              </div>
              {isTraditionalLTCQuote(displayQuote) && (
                <>
                  <PartnershipEligibleBadge
                    policyType={getPolicyTypeFromTraditionalLTCQuote(displayQuote)}
                    state={
                      zipCodeToState(client?.intakeSurvey?.clientZipCode)?.stateAbbreviation ??
                      displayQuote.state
                    }
                    primaryAge={displayQuote.primaryAge}
                    bioPercent={displayQuote.bioPercent}
                  />
                  <PartnershipEligibilityDisclaimer
                    policyType={getPolicyTypeFromTraditionalLTCQuote(displayQuote)}
                    state={
                      zipCodeToState(client?.intakeSurvey?.clientZipCode)?.stateAbbreviation ??
                      displayQuote.state
                    }
                    primaryAge={displayQuote.primaryAge}
                    bioPercent={displayQuote.bioPercent}
                  />
                </>
              )}
            </div>
          </div>

          {/* Metrics Section + Chevron - Right Side */}
          <div className="flex items-center gap-4">
            <div className="flex items-start gap-8">
              {/* Projected Coverage */}
              <div className="flex flex-col gap-0.5">
                <div className="text-xs font-medium text-gray-700">Projected Coverage</div>
                <div className="text-primary text-xl font-bold">
                  {formatCurrency(projectedCoverage)}
                </div>
                {displayROI && (
                  <Badge className="bg-primary-50 text-primary hover:bg-primary-50 mt-0.5 w-fit rounded-full px-2 py-0.5 text-[10px] font-medium">
                    ROI {displayROI}
                  </Badge>
                )}
              </div>

              {/* Cost */}
              <div className="flex flex-col gap-0.5">
                <div className="text-xs font-medium text-gray-700">Cost</div>
                <div className="text-xl font-bold text-gray-900">
                  <LtcCostDisplay value={costValue} />
                </div>
                <div className="text-[10px] italic text-gray-700">{costLabel}</div>
              </div>
            </div>

            {/* Expand/Collapse Button */}
            <Button
              variant="ghost"
              size="icon"
              onClick={event => {
                event.stopPropagation();
                onToggleExpand();
              }}
              className="text-primary group-hover:bg-primary-200/30 rounded-full transition-colors"
              aria-label={isExpanded ? 'Collapse' : 'Expand'}
            >
              {isExpanded ? (
                <ChevronDown className="size-7" />
              ) : (
                <ChevronRight className="size-7" />
              )}
            </Button>
          </div>
        </div>

        {/* Policy Details - Only when expanded */}
        {isExpanded && (
          <>
            <div className="px-6 pb-4">
              {isLifeInsuranceQuote(displayQuote) ? (
                <ProductDetails quote={displayQuote} />
              ) : isTraditionalLTCQuote(displayQuote) ? (
                <TraditionalLTCProductDetails quote={displayQuote} />
              ) : null}
            </div>

            {hasCustomization && customization.isCustomizing && (
              <CustomizationPanel
                quote={displayQuote as Quote}
                selectedPremium={customization.selectedPremium}
                onPremiumChange={customization.setSelectedPremium}
                selectedBenefitPeriod={customization.selectedBenefitPeriod}
                onBenefitPeriodChange={customization.setSelectedBenefitPeriod}
                selectedInflationOption={customization.selectedInflationOption}
                onInflationOptionChange={customization.setSelectedInflationOption}
                selectedPaymentType={customization.selectedPaymentType}
                onPaymentTypeChange={customization.setSelectedPaymentType}
                availableOptions={customization.availableOptions}
                totalMatchingQuotes={customization.totalMatchingQuotes}
                hasFilters={customization.hasFilters}
                onResetFilters={customization.resetFilters}
                sliderBounds={customization.sliderBounds}
                onClose={() => customization.setIsCustomizing(false)}
              />
            )}

            {hasCustomization && !customization.isCustomizing && !isSelected && (
              <div className="px-6 pb-4">
                <Button
                  variant="outline"
                  onClick={() => customization.setIsCustomizing(true)}
                  className="w-full"
                >
                  <Settings className="size-4" />
                  Customize Coverage
                </Button>
              </div>
            )}
          </>
        )}

        {/* Divider */}
        <div className="border-t border-gray-200" />

        {/* Action Buttons Row */}
        <div className="flex items-center justify-end gap-2 px-6 py-4">
          <Button
            variant="outline"
            onClick={onCompare}
            size="sm"
            className="rounded-xl px-6"
          >
            Compare
          </Button>
          <Button
            onClick={handleTogglePolicy}
            size="sm"
            variant={isSelected ? 'secondary' : 'default'}
            className="rounded-xl px-6"
            disabled={isLoading}
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isSelected ? 'Removing...' : 'Adding...'}
              </>
            ) : (
              <>{isSelected ? 'Remove' : 'Add to Plan'}</>
            )}
          </Button>
        </div>
      </div>
    </Card>
  );
}
